import { ReactElement, memo } from "react";

import { FlashList, type ListRenderItem } from "@/components/atoms/FlashList";
import { Heading } from "@/components/atoms/Heading";
import { PlayerCardData } from "@/types/playerCardTypes";
import { ifWeb } from "@/utils/platform";

/**
 * Props for the ListPlayerCompareView component
 */
interface IListPlayerCompare {
  data?: PlayerCardData[];
  title: string;
  showsVertical: boolean;
  keyExtractor: (item: PlayerCardData) => string;
  onEndReached: () => void;
  renderPlayer: ListRenderItem<PlayerCardData> | null | undefined;
  renderListFooterComponent: () => ReactElement | null;
  renderListEmptyComponent: () => ReactElement | null;
}

/**
 * View component for rendering a scrollable list of players
 * Uses FlatList with header, footer, and empty state components
 * @param root0 - Component props
 * @param root0.data - Array of players to render in the list
 * @param root0.keyExtractor - Memoized function to extract unique key for each item
 * @param root0.onEndReached - Memoized callback triggered when scrolling to end of list
 * @param root0.renderListEmptyComponent - Function to render when list is empty
 * @param root0.renderListFooterComponent - Function to render footer (loading indicator)
 * @param root0.title - Title displayed in list header
 * @param root0.showsVertical - Whether to show vertical scroll indicator
 * @param root0.renderPlayer - Function to render each player item
 */
const ListPlayerCompareView = ({
  data,
  keyExtractor,
  onEndReached,
  renderListEmptyComponent,
  renderListFooterComponent,
  title,
  showsVertical,
  renderPlayer,
}: IListPlayerCompare) => (
  <FlashList
    data={data}
    keyExtractor={keyExtractor}
    onEndReached={onEndReached}
    ListHeaderComponent={
      <Heading variant={ifWeb("title-sm", "title")} className="pb-4">
        {title}
      </Heading>
    }
    ListFooterComponent={renderListFooterComponent}
    ListEmptyComponent={renderListEmptyComponent}
    renderItem={renderPlayer}
    scrollEventThrottle={16}
    className="min-h-20"
    contentContainerClassName="flex-grow p-4"
    showsVerticalScrollIndicator={showsVertical}
  />
);

ListPlayerCompareView.displayName = "ListPlayerCompareView";

export default memo(ListPlayerCompareView);
